import { Dispatch } from 'react';
import { HttpHeaders } from 'models';
import { AxiosError, AxiosResponse } from 'axios';
import i18n from 'i18next';

//project imports
import {
  createGetHeaderData,
  createHeaderData,
  createQueryParams,
  handlePreconditionFailed
} from 'utils/restUtils';
import { apiClient } from 'services/restService';
import {
  BalanceGroupDetailInterface,
  UpdateBalanceGroupRequestBody
} from 'features/eai/eaiTypes';
import { openSnackbar } from 'features/app/appSlice';
import {
  setBalanceGroupDeliveryPointList,
  setBalanceGroupDetail,
  setBalanceGroupsList
} from 'features/eai/eaiSlice';

//types
import { AppThunk } from 'models/store';
import { EaiEtagEntityList } from 'models/etag';
import { logError } from 'services/Logging';
import {
  CharacteristicValueInterface,
  UpdateCharacteristicValueRequestBody
} from 'models/characteristics';

const balanceGroupPrefix = '/eai/balancing-group';

export const fetchAndSetBalanceGroupDetailByBalanceGroupId =
  (id?: string | bigint): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setBalanceGroupDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );
    apiClient
      .get(`${balanceGroupPrefix}/${id}/detail`, {
        headers,
        data: { etagEntity: EaiEtagEntityList.BALANCE_GROUP }
      })
      .then((response: AxiosResponse) => {
        dispatch(setBalanceGroupDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchAndSetBalanceGroupDetailByMarketParticipantId =
  (
    id?: string | bigint
  ): AppThunk<Promise<BalanceGroupDetailInterface | undefined>> =>
  async (dispatch: Dispatch<ReturnType<typeof setBalanceGroupDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    try {
      const response = await apiClient.get(
        `${balanceGroupPrefix}/market-participant/${id}`,
        {
          headers,
          data: { etagEntity: EaiEtagEntityList.BALANCE_GROUP }
        }
      );
      const responseData = response.data as
        | BalanceGroupDetailInterface
        | undefined;
      if (responseData) {
        dispatch(setBalanceGroupDetail(responseData));
      }
      return responseData;
    } catch (error: unknown) {
      logError(error);
    }
  };

export const getBalanceGroupCharacteristics = async (
  id: string
): Promise<CharacteristicValueInterface[]> => {
  let characteristics: CharacteristicValueInterface[] = [];
  const headers: HttpHeaders = createGetHeaderData(
    true,
    'application/hal+json'
  );

  try {
    const response = await apiClient.get(
      `${balanceGroupPrefix}/${id}/characteristics`,
      { headers }
    );
    if (response.data.characteristics) {
      characteristics = response.data.characteristics;
    }
  } catch (error) {
    logError(error);
  }

  return characteristics;
};

export const updateBalanceGroupCharacteristics =
  (
    id: string,
    requestBody: UpdateCharacteristicValueRequestBody,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<
        | typeof openSnackbar
        | typeof fetchAndSetBalanceGroupDetailByBalanceGroupId
      >
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `${balanceGroupPrefix}/${id}/characteristics`,
        { ...requestBody },
        { headers }
      )
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.eai.messages.updateBalanceGroupCharacteristics'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetBalanceGroupDetailByBalanceGroupId(id)),
          () =>
            updateBalanceGroupCharacteristics(
              id,
              requestBody,
              undefined,
              callback
            )
        );
        logError(error);
      });
  };

export const getBalanceGroupDeliveryPointList =
  (id?: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setBalanceGroupDeliveryPointList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );
    apiClient
      .get(`${balanceGroupPrefix}/${id}/delivery-points`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setBalanceGroupDeliveryPointList(response.data));
      });
  };

export const updateBalanceGroup =
  (
    requestBody: UpdateBalanceGroupRequestBody,
    id?: string | bigint,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<
        | typeof openSnackbar
        | typeof fetchAndSetBalanceGroupDetailByBalanceGroupId
      >
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `${balanceGroupPrefix}/${id}/detail`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(fetchAndSetBalanceGroupDetailByBalanceGroupId(id));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateBalanceGroup'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetBalanceGroupDetailByBalanceGroupId(id)),
          () =>
            dispatch(updateBalanceGroup(requestBody, id, undefined, callback))
        );
        logError(error);
      });
  };

export const getBalanceGroupsList =
  (search?: string, size?: number, page?: number, sort?: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setBalanceGroupsList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    const queryParams = createQueryParams({
      search: search,
      size: size || -1,
      page: page,
      sort: sort
    });

    apiClient
      .get(`${balanceGroupPrefix}${queryParams}`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setBalanceGroupsList(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };
